import React from 'react';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Brain,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Sparkles,
  ChevronDown,
  Send,
  Loader2,
  Wrench,
  TrendingUp,
} from 'lucide-react';

/**
 * CandidateAIBrief — Bloc d'analyse IA principal, en HAUT de la fiche candidat.
 *
 * Lecture rapide (sans scroll) :
 *   • Score IA + décision (À recruter / À tester / Rejeter)
 *   • Niveau global
 *   • Résumé IA (2-3 lignes)
 *   • Top 3 forces · Top 1-2 risques
 *   • Outils maîtrisés (chips, depuis ai_analysis.tools / tools_mastered)
 *   • Recommandation finale
 *   • Bouton "Analyse complète" → bascule sur l'onglet Qualification
 *
 * Si pas encore de qualif : encart neutre avec CTA d'envoi d'évaluation.
 */

const decisionMap = {
  A_RECRUTER: { label: 'À recruter', cls: 'bg-green-600', icon: CheckCircle2 },
  A_TESTER: { label: 'À tester', cls: 'bg-orange-500', icon: Clock },
  REJETER: { label: 'À rejeter', cls: 'bg-red-600', icon: XCircle },
};

const scoreColorClass = (color) => {
  switch (color) {
    case 'green':
      return 'text-green-600 border-green-500';
    case 'orange':
      return 'text-orange-600 border-orange-500';
    case 'red':
      return 'text-red-600 border-red-500';
    default:
      return 'text-gray-600 border-gray-300';
  }
};

const CandidateAIBrief = ({ qualification, candidate, sendEvaluationForm, sendingEval }) => {
  // --- Empty state (no qualif yet) ---
  if (!qualification || !qualification.score_total) {
    return (
      <Card
        className="p-4 md:p-5 border-dashed border-tp-border bg-tp-bg/40"
        data-testid="ai-brief-empty"
      >
        <div className="flex items-start gap-3 flex-wrap">
          <div className="h-10 w-10 rounded-lg bg-tp-accent/10 text-tp-accent flex items-center justify-center flex-shrink-0">
            <Brain className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display text-sm font-semibold text-tp-text">Analyse IA</h3>
              <Badge className="bg-gray-100 text-gray-700 border border-gray-200">
                Non disponible
              </Badge>
            </div>
            <p className="text-xs text-tp-text-light mt-1 leading-relaxed">
              Le candidat n&apos;a pas encore rempli le formulaire d&apos;évaluation. Envoyez-le pour obtenir une analyse IA détaillée.
            </p>
          </div>
          {sendEvaluationForm && (
            <Button
              size="sm"
              onClick={sendEvaluationForm}
              disabled={sendingEval}
              data-testid="ai-brief-send-form"
              className="flex-shrink-0"
            >
              {sendingEval ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Send className="h-4 w-4 mr-1.5" />
                  Envoyer l&apos;évaluation
                </>
              )}
            </Button>
          )}
        </div>
      </Card>
    );
  }

  const {
    score_total = 0,
    score_color = 'gray',
    niveau_label = '',
    decision = '',
    decision_label = '',
    forces = [],
    risques = [],
    faiblesses = [],
    profile_title = '',
    profile_type = '',
    ai_analysis: qualifAi = {},
  } = qualification;

  const dec = decisionMap[decision] || {
    label: decision_label || '—',
    cls: 'bg-gray-500',
    icon: AlertTriangle,
  };
  const DecIcon = dec.icon;
  const top3Forces = (forces || []).slice(0, 3);
  const topRisks = ((risques && risques.length ? risques : faiblesses) || []).slice(0, 2);

  // === TOOLS — fusion ai_analysis.tools (CV) + tools_mastered (declared) ===
  const aiTools = candidate?.ai_analysis?.tools || [];
  const declaredTools = candidate?.tools_mastered || qualification?.tools_mastered || [];
  const allTools = Array.from(
    new Set([...declaredTools, ...aiTools].map((t) => (typeof t === 'string' ? t : '')).filter(Boolean))
  );

  // Summary — prefer qualif AI summary, fall back to candidate AI summary
  const summary =
    qualifAi?.summary ||
    qualifAi?.global_assessment ||
    candidate?.ai_analysis?.summary ||
    null;

  // Recommendation text
  const recommendation =
    qualifAi?.recommendation ||
    qualifAi?.recommandation ||
    qualification?.recommendation ||
    null;

  const scrollToQualif = () => {
    const trigger = document.querySelector('[role="tab"][value="qualification"]');
    if (trigger) {
      trigger.click();
      setTimeout(() => trigger.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    } else {
      const tabsEl = document.querySelector('[role="tablist"]');
      tabsEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Card
      className="p-4 md:p-5 border-tp-border bg-white shadow-sm"
      data-testid="ai-brief-card"
    >
      {/* --- TOP ROW : score + headline + decision badge + CTA --- */}
      <div className="flex items-start gap-3 sm:gap-4 flex-wrap">
        <div
          className={`flex-shrink-0 h-16 w-16 sm:h-20 sm:w-20 rounded-full border-2 flex flex-col items-center justify-center ${scoreColorClass(
            score_color
          )} bg-white shadow-sm`}
          data-testid="ai-brief-score"
        >
          <span className="text-xl sm:text-2xl font-bold leading-none tabular-nums">
            {score_total}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-tp-text-light mt-0.5">
            /100
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Sparkles className="h-4 w-4 text-tp-accent flex-shrink-0" />
            <h3 className="font-display text-sm sm:text-base font-semibold text-tp-text">
              Analyse IA — {profile_title || profile_type || 'Candidat'}
            </h3>
            <Badge
              className={`${dec.cls} text-white border-0 inline-flex items-center gap-1`}
              data-testid="ai-brief-decision"
            >
              <DecIcon className="h-3 w-3" />
              {dec.label}
            </Badge>
          </div>
          {niveau_label && (
            <p className="text-xs text-tp-text-light mt-1">
              Niveau global : <strong className="text-tp-text">{niveau_label}</strong>
            </p>
          )}
        </div>

        <Button
          size="sm"
          variant="outline"
          onClick={scrollToQualif}
          className="flex-shrink-0"
          data-testid="ai-brief-expand"
        >
          <ChevronDown className="h-4 w-4 mr-1.5" />
          <span className="hidden sm:inline">Analyse complète</span>
          <span className="sm:hidden">Détails</span>
        </Button>
      </div>

      {/* --- AI SUMMARY --- */}
      {summary && (
        <div
          className="mt-4 p-3 rounded-lg bg-tp-bg/40 border border-tp-border text-sm text-tp-text leading-relaxed"
          data-testid="ai-brief-summary"
        >
          {summary}
        </div>
      )}

      {/* --- 3-COL : forces / risques / outils --- */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Forces */}
        {top3Forces.length > 0 && (
          <div className="bg-green-50/60 border border-green-200 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-green-700 uppercase tracking-wider mb-2">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Forces
            </div>
            <ul className="space-y-1" data-testid="ai-brief-forces">
              {top3Forces.map((f, i) => (
                <li key={i} className="text-[13px] text-tp-text leading-snug">
                  · {typeof f === 'string' ? f : f?.text || ''}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Risques / faiblesses */}
        {topRisks.length > 0 && (
          <div className="bg-orange-50/60 border border-orange-200 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-orange-700 uppercase tracking-wider mb-2">
              <AlertTriangle className="h-3.5 w-3.5" />
              Points d&apos;attention
            </div>
            <ul className="space-y-1" data-testid="ai-brief-risks">
              {topRisks.map((r, i) => (
                <li key={i} className="text-[13px] text-tp-text leading-snug">
                  · {typeof r === 'string' ? r : r?.text || ''}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Outils maîtrisés */}
        {allTools.length > 0 && (
          <div className="bg-blue-50/40 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 uppercase tracking-wider mb-2">
              <Wrench className="h-3.5 w-3.5" />
              Outils maîtrisés
            </div>
            <div className="flex flex-wrap gap-1" data-testid="ai-brief-tools">
              {allTools.slice(0, 12).map((tool) => (
                <span
                  key={tool}
                  className="inline-block bg-white border border-blue-200 text-blue-800 text-[11px] font-medium px-2 py-0.5 rounded-full"
                >
                  {tool}
                </span>
              ))}
              {allTools.length > 12 && (
                <span className="inline-block text-[11px] text-blue-600 px-2 py-0.5">
                  +{allTools.length - 12}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* --- RECOMMENDATION --- */}
      {recommendation && (
        <div
          className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-tp-primary/5 border border-tp-primary/20"
          data-testid="ai-brief-recommendation"
        >
          <TrendingUp className="h-4 w-4 text-tp-primary mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-tp-primary mb-0.5">
              Recommandation
            </div>
            <div className="text-[13px] text-tp-text leading-snug">{recommendation}</div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default CandidateAIBrief;
